import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import sortBy from 'lodash.sortby';
import { styled } from '@mui/material/styles';
import ComboLocationSelect, { LOCATION_TYPE } from '@features/ComboLocationSelect';
import { Input } from '@components/Input';
import { Checkbox } from '@components/Checkbox';
import { Switch } from '@components/Switch';
import { MultiSelect } from '@components/MultiSelect';
import { Typography } from '@components/Typography';
import { useTranslation, useLocations, useChannels } from '@hooks';

const PlatformWrapper = styled('div')({
    display: 'flex',
    alignItems: 'center',
    transform: 'translateX(-7px)',
    '& .MuiCheckbox-root': {
        padding: 4,
    },
});

const FieldWrapper = styled('div')({
    display: 'grid',
    gridTemplateColumns: 'calc(50% - 8px) calc(50% - 8px)',
    gridGap: 16,
    marginTop: '8px',
    alignItems: 'center',
});

const GeneralFields = ({ isView, brands, selectedAgentRef, isEdit }) => {
    const { t } = useTranslation();
    const { control, setValue, getValues, watch } = useFormContext();
    const { getSelectLocationsOptions } = useLocations();
    const { getChannelsByBrand, getChannelsByLocationIdStrict, getChannelOptions } = useChannels();

    const emailFieldEnabled = watch('contactEmailEnabled');
    const phoneFieldEnabled = watch('contactPhoneNumberEnabled');
    const nameFieldEnabled = watch('contactNameEnabled');
    const locationType = watch('locationType');
    const brandId = watch('brandId');
    const locationId = watch('locationId');

    const brandsOptions = useMemo(() => brands.map(item => ({ value: item.id, label: item.title })), [brands]);

    const locationsOptions = useMemo(() => getSelectLocationsOptions(), []);

    const channelsOptions = useMemo(() => {
        const availableChannels =
            locationType === LOCATION_TYPE.BRAND
                ? getChannelsByBrand(brandId)
                : getChannelsByLocationIdStrict(locationId);
        // INFO: withDisconnected to show disconnected channels in options when editing or viewing
        return sortBy(
            getChannelOptions(
                {
                    in: true,
                    smsTwoWay: true,
                    withDisconnected: isEdit || isView,
                    withDisabledAgents: true,
                    currentAgent: selectedAgentRef.current?.id,
                },
                availableChannels,
            ),
            ['name'],
        );
    }, [locationType, brandId, locationId]);

    return (
        <>
            <Controller
                control={control}
                name="name"
                render={({ field, fieldState: { error }, ref }) => (
                    <Input
                        ref={ref}
                        inputProps={{ ...field, onChange: e => field.onChange(e.target.value) }}
                        label={t('common.name')}
                        fullWidth
                        error={!!error}
                        helperText={error ? t(error.message) : null}
                        required
                        disabled={isView}
                    />
                )}
            />
            <ComboLocationSelect
                brandsOptions={brandsOptions}
                locationsOptions={locationsOptions}
                data={selectedAgentRef.current}
                disabled={isView}
                isView={isView}
                updatedLocationCb={() => {
                    setValue('channels', [], { shouldDirty: true });
                    setValue('surveyTemplateId', '', { shouldDirty: true });
                }}
                updatedLocationTypeCb={() => {
                    setValue('channels', [], { shouldDirty: true });
                    setValue('surveyTemplateId', '', { shouldDirty: true });
                }}
            />
            <Controller
                control={control}
                name="channels"
                render={({ field, fieldState: { error }, ref }) => (
                    <MultiSelect
                        {...field}
                        ref={ref}
                        onChange={value => {
                            setValue('channels', value, { shouldValidate: true, shouldDirty: true });
                        }}
                        options={channelsOptions}
                        label={t('filters.channels')}
                        fullWidth
                        countable
                        nameBased
                        required
                        disabled={isView}
                        error={error}
                    />
                )}
            />
            <Controller
                control={control}
                name="status"
                render={({ field, ref }) => (
                    <Switch
                        {...field}
                        ref={ref}
                        checked={getValues('status')}
                        handleChange={event => {
                            setValue('status', event.target.checked, { shouldDirty: true });
                        }}
                        label={t('Agents.activateAgent')}
                        disabled={isView}
                        sx={{ marginTop: '-16px' }}
                    />
                )}
            />
            <div>
                <Typography variant="subtitle1" sx={{ color: 'rgba(0, 0, 0, 0.87)' }}>
                    {t('FeedbackSurveys.contactDetails')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(0, 0, 0, 0.6)', display: 'block', marginTop: '4px' }}>
                    {t('Agents.contactDetailsDescription')}
                </Typography>
                <FieldWrapper>
                    <Controller
                        control={control}
                        name="contactEmailEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={getValues('contactEmailEnabled')}
                                handleChange={event => {
                                    setValue('contactEmailEnabled', event.target.checked, { shouldDirty: true });
                                    if (!event.target.checked) {
                                        setValue('contactEmailRequired', false, { shouldDirty: true });
                                    }
                                }}
                                label={t('FeedbackSurveys.contactEmail')}
                                disabled={isView}
                            />
                        )}
                    />
                    {emailFieldEnabled ? (
                        <PlatformWrapper>
                            <Controller
                                control={control}
                                name="contactEmailRequired"
                                render={({ field, ref }) => (
                                    <Checkbox
                                        ref={ref}
                                        disabled={isView}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        onChange={e => {
                                            setValue('contactEmailRequired', e.target.checked, {
                                                shouldDirty: true,
                                            });
                                        }}
                                        checked={field.value}
                                    />
                                )}
                            />
                            <Typography variant="body2">{t('common.required')}</Typography>
                        </PlatformWrapper>
                    ) : (
                        <div />
                    )}
                </FieldWrapper>
                <FieldWrapper>
                    <Controller
                        control={control}
                        name="contactPhoneNumberEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={getValues('contactPhoneNumberEnabled')}
                                handleChange={event => {
                                    setValue('contactPhoneNumberEnabled', event.target.checked, { shouldDirty: true });
                                    if (!event.target.checked) {
                                        setValue('contactPhoneNumberRequired', false, { shouldDirty: true });
                                    }
                                }}
                                label={t('FeedbackSurveys.contactPhone')}
                                disabled={isView}
                            />
                        )}
                    />
                    {phoneFieldEnabled ? (
                        <PlatformWrapper>
                            <Controller
                                control={control}
                                name="contactPhoneNumberRequired"
                                render={({ field, ref }) => (
                                    <Checkbox
                                        ref={ref}
                                        disabled={isView}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        onChange={e => {
                                            setValue('contactPhoneNumberRequired', e.target.checked, {
                                                shouldDirty: true,
                                            });
                                        }}
                                        checked={field.value}
                                    />
                                )}
                            />
                            <Typography variant="body2">{t('common.required')}</Typography>
                        </PlatformWrapper>
                    ) : (
                        <div />
                    )}
                </FieldWrapper>
                <FieldWrapper>
                    <Controller
                        control={control}
                        name="contactNameEnabled"
                        render={({ field, ref }) => (
                            <Switch
                                {...field}
                                ref={ref}
                                checked={getValues('contactNameEnabled')}
                                handleChange={event => {
                                    setValue('contactNameEnabled', event.target.checked, { shouldDirty: true });
                                    if (!event.target.checked) {
                                        setValue('contactNameRequired', false, { shouldDirty: true });
                                    }
                                }}
                                label={t('FeedbackSurveys.contactName')}
                                disabled={isView}
                            />
                        )}
                    />
                    {nameFieldEnabled ? (
                        <PlatformWrapper>
                            <Controller
                                control={control}
                                name="contactNameRequired"
                                render={({ field, ref }) => (
                                    <Checkbox
                                        ref={ref}
                                        disabled={isView}
                                        inputProps={{ 'aria-label': 'controlled' }}
                                        onChange={e => {
                                            setValue('contactNameRequired', e.target.checked, {
                                                shouldDirty: true,
                                            });
                                        }}
                                        checked={field.value}
                                    />
                                )}
                            />
                            <Typography variant="body2">{t('common.required')}</Typography>
                        </PlatformWrapper>
                    ) : (
                        <div />
                    )}
                </FieldWrapper>
            </div>
        </>
    );
};

export default GeneralFields;
